import * as React from 'react';

import {
  Button,
  cn,
  IconButton,
  Popover,
  PopoverTrigger,
  Tooltip,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucidePackageMinus } from 'lucide-react';

import { $deductionTypes } from '~/modules/trade/_state/loadReconciliation.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface AddDeductionButtonProps {
  onAddDeduction: (deductionTypeId: string) => void;
  isDisabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  variant?: 'outline' | 'solid';
  color?: 'failure' | 'primary' | 'neutral';
  iconButton?: boolean;
}

export const AddDeductionButton: React.FC<AddDeductionButtonProps> = ({
  onAddDeduction,
  isDisabled = false,
  children,
  className = '',
  variant = 'outline',
  color,
  iconButton = false,
}) => {
  const t = useTranslations();
  const { data: deductionTypes } = useAtomValue($deductionTypes);
  const [isOpen, setIsOpen] = React.useState(false);

  const handleDeductionSelect = (deductionTypeId: string) => {
    onAddDeduction(deductionTypeId);
    setIsOpen(false);
  };

  return (
    <PopoverTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {iconButton ? (
        <Tooltip
          tip={t.loadStatusTransition.reconciliation.actions.addDeduction}
          wrapWithButton={false}
        >
          <IconButton
            icon={<LucidePackageMinus size={18} />}
            variant={variant || 'outline'}
            className={cn('size-[40px]', className)}
            color={isOpen ? 'primary' : color}
            aria-label="Add deduction to received line item"
            onPress={() => setIsOpen(!isOpen)}
            isDisabled={isDisabled}
          />
        </Tooltip>
      ) : (
        <Button
          variant={variant}
          color={color}
          isDisabled={isDisabled}
          className={className}
        >
          {children ||
            t.loadStatusTransition.reconciliation.actions.addDeduction}
        </Button>
      )}
      <Popover className="border-none">
        <div className="no-scrollbar flex max-h-[30vh] w-[220px] flex-col items-center gap-[1px] overflow-auto rounded-md border-[1px] border-neutral/20 bg-neutral shadow-lg">
          {deductionTypes?.map((type) => (
            <div
              key={type.id}
              className="group flex w-full cursor-pointer items-center rounded py-2 pr-6 pl-3 text-sm text-abs-white outline-0 select-none hover:bg-primary-darker hover:text-abs-white"
              onClick={() => handleDeductionSelect(type.id)}
            >
              {type.name}
            </div>
          ))}
          {!deductionTypes && (
            <div className="py-2 pr-6 pl-3 text-sm text-abs-white opacity-50">
              {t.materialsReconciliation.addDeductionButton.loading}
            </div>
          )}
        </div>
      </Popover>
    </PopoverTrigger>
  );
};
